import { useEffect, useState } from 'react';
import { organizationService } from '@/services/api/organization.service';

/**
 * Suggests a booking address from the business name and checks whether it is
 * free, debounced.
 *
 * Shared by the password registration form and the Google step-0 form so the
 * transliteration table below exists once. It mirrors the
 * `i18n.transliterate.rule` entries in config/locales/*.yml, which is what
 * `Organization#generate_subdomain` applies server-side — if the two drift, the
 * live preview stops matching the address the user actually gets.
 */
const DIACRITICS: Record<string, string> = {
  // Polish
  'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
  'Ą': 'a', 'Ć': 'c', 'Ę': 'e', 'Ł': 'l', 'Ń': 'n', 'Ó': 'o', 'Ś': 's', 'Ź': 'z', 'Ż': 'z',
  // German
  'ä': 'a', 'ö': 'o', 'ü': 'u', 'ß': 'ss',
  'Ä': 'a', 'Ö': 'o', 'Ü': 'u',
  // Portuguese
  'á': 'a', 'â': 'a', 'ã': 'a', 'à': 'a', 'ç': 'c', 'é': 'e', 'ê': 'e', 'í': 'i', 'ô': 'o', 'õ': 'o', 'ú': 'u',
  'Á': 'a', 'Â': 'a', 'Ã': 'a', 'À': 'a', 'Ç': 'c', 'É': 'e', 'Ê': 'e', 'Í': 'i', 'Ô': 'o', 'Õ': 'o', 'Ú': 'u',
};

export const SUBDOMAIN_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function suggestSubdomain(businessName: string): string {
  return businessName
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻäöüßÄÖÜáâãàçéêíôõúÁÂÃÀÇÉÊÍÔÕÚ]/g, (c) => DIACRITICS[c] ?? c)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

/**
 * Debounced availability check. Returns null while unknown (too short,
 * malformed, still typing, or the lookup failed) so callers can distinguish
 * "not checked" from "taken".
 */
export function useSubdomainAvailability(subdomain: string | undefined) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const normalized = subdomain?.toLowerCase().trim();

    if (!normalized || normalized.length < 3 || !SUBDOMAIN_PATTERN.test(normalized)) {
      setAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const result = await organizationService.checkSubdomainAvailability(normalized);
        setAvailable(result.available);
      } catch (error) {
        console.error('Failed to check subdomain availability:', error);
        setAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [subdomain]);

  return { available, checking };
}
